// =============================================================================
// chartColors.ts — single source of truth for ALL chart + PPT-export colors.
// -----------------------------------------------------------------------------
// echarts renders to <canvas>, so it needs literal hex values (not var(--x)).
// We read the brand tokens from CSS at call time via getComputedStyle so charts
// stay in sync with _tokens.scss (and, once a theme toggle exists, with dark
// mode) — with a hard-coded LIGHT fallback for tests/SSR and for PPT export
// (slides are always on a white background, so PPT uses the light fallback).
//
// Categorical assignments preserve rough hue families from the old palette so
// categories stay recognizable: diesel≈blue, gas≈warm, electric≈purple, etc.
// =============================================================================

// Light-theme fallbacks — MUST mirror --chart-1..6 in _tokens.scss.
const FALLBACK: Record<string, string> = {
  '--chart-1': '#1C9AD6', // brand blue
  '--chart-2': '#6BAA2C', // brand green
  '--chart-3': '#F7A73F', // brand amber
  '--chart-4': '#F5586B', // brand coral
  '--chart-5': '#7E57C2', // extension violet
  '--chart-6': '#26A69A', // extension teal
  '--chart-1-soft': '#9AD3EF', // light brand blue (secondary line series)
  '--text': '#1E2530',
  '--text-muted': '#5E5952',
  '--border': '#E4E0D8',
  '--surface-elevated': '#FFFFFF',
  '--success': '#427A17',
  '--warning': '#F7A73F',
  '--danger': '#CE2C3E',
};

/** Read a CSS custom property at call time; fall back to the light literal. */
function tok(name: keyof typeof FALLBACK): string {
  if (typeof window !== 'undefined' && typeof document !== 'undefined' && document.documentElement) {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (v) return v;
  }
  return FALLBACK[name];
}

/** Always the light literal (for PPT export — slides are on white). */
function litLight(name: keyof typeof FALLBACK): string {
  return FALLBACK[name];
}

/** pptxgenjs wants hex WITHOUT the leading '#'. */
export const noHash = (hex: string): string => hex.replace('#', '').toUpperCase();

// ---- categorical palette (rotating series) ----------------------------------
export const chartPalette = (): string[] =>
  ['--chart-1', '--chart-2', '--chart-3', '--chart-4', '--chart-5', '--chart-6'].map((n) => tok(n as keyof typeof FALLBACK));

export const chartPaletteLight = (): string[] =>
  ['--chart-1', '--chart-2', '--chart-3', '--chart-4', '--chart-5', '--chart-6'].map((n) => litLight(n as keyof typeof FALLBACK));

// ---- single/dual line-series colors -----------------------------------------
export const seriesPrimary = (): string => tok('--chart-1');
export const seriesSecondary = (): string => tok('--chart-1-soft');
export const seriesPrimaryLight = (): string => litLight('--chart-1');
export const seriesSecondaryLight = (): string => litLight('--chart-1-soft');

// ---- fixed category maps (recognizable, on-brand) ---------------------------
// Diesel≈blue, LNG≈amber, CNG≈teal, Electricity≈violet.
export const fuelColors = (): Record<string, string> => {
  const p = chartPalette();
  return {
    Diesel: p[0],
    'Diesel (B7 blend)': p[0],
    LNG: p[2],
    CNG: p[5],
    Elektriciteit: p[4],
  };
};

// FUEL≈amber, CPI≈blue, MODALITY≈green, NONE≈muted.
export const sourceColors = (): Record<string, string> => {
  const p = chartPalette();
  return { FUEL: p[2], CPI: p[0], MODALITY: p[1], NONE: tok('--text-muted') };
};

// unknown categories rotate through the palette
export const fallbackColors = (): string[] => chartPalette();
export const modalityColors = (): string[] => chartPalette();

// ---- semantic (good / warning / bad) — e.g. error-rate thresholds -----------
export const semanticColors = () => ({
  good: tok('--success'),
  warn: tok('--warning'),
  bad: tok('--danger'),
});

// ---- chart chrome (axes, gridlines, labels, backgrounds) --------------------
export const chartChrome = () => ({
  text: tok('--text'),
  muted: tok('--text-muted'),
  grid: tok('--border'),
  surface: tok('--surface-elevated'),
});
